import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';

export interface Diagnosis {
    id: string;
    code: string;
    name: string;
    status: 'Active' | 'Inactive';
}

export interface DiagnosisWritePayload {
    code?: string;
    name?: string;
    status?: 'Active' | 'Inactive';
}

function normalizeStatus(s: string): 'Active' | 'Inactive' {
    return String(s).toLowerCase() === 'active' ? 'Active' : 'Inactive';
}

function normalize(row: { id: string; code: string; name: string; status: string }): Diagnosis {
    return { id: row.id, code: row.code || '', name: row.name, status: normalizeStatus(row.status) };
}

async function fetchDiagnoses(): Promise<Diagnosis[]> {
    const res = await api.get<Array<{ id: string; code: string; name: string; status: string }>>('/diagnoses');
    return res.data.map(normalize);
}

export function useDiagnoses() {
    return useQuery({
        queryKey: ['diagnoses'] as const,
        queryFn: fetchDiagnoses,
        staleTime: 60_000,
    });
}

export function useCreateDiagnosis() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (payload: DiagnosisWritePayload) => {
            const res = await api.post('/diagnoses', payload);
            return normalize(res.data);
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['diagnoses'] }),
    });
}

export function useUpdateDiagnosis() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, payload }: { id: string; payload: DiagnosisWritePayload }) => {
            const res = await api.patch(`/diagnoses/${id}`, payload);
            return normalize(res.data);
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['diagnoses'] }),
    });
}

export function useDeleteDiagnosis() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (id: string) => {
            await api.delete(`/diagnoses/${id}`);
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['diagnoses'] }),
    });
}
